/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { Block, Typography } from '@atoms';
import Sortable from '@atoms/Sortable';
import CONTENT_MODULES, { presets } from '@constants/contentModules';
import ContentModulePicker from '@molecules/ContentModulePicker';
import { useModalControls, useSearchParam } from '@hooks';
import { showDeleteConfirm } from '@utils/modal';

import Form from '../Form';

const ControlledContentModuleModal = ({ component: ContentModuleModal, children, ...props }) => {
  const modalControls = useModalControls(false);

  return (
    <>
      <ContentModuleModal {...modalControls} {...props} />
      {children(modalControls)}
    </>
  );
};

const ContentModulesField = ({ label, form, name, preset, parentId, parentType, allowAdd, eventBlogsList }) => {
  const [contentModuleTypeParam] = useSearchParam({ key: 'contentModule' });

  const handleSubmit = (values) => {
    const contentModules = form.getFieldValue(name) || [];
    form.setFields([{ name, value: [...contentModules, values] }]);
  };

  const [contentModules, setContentModules] = React.useState([]);

  React.useEffect(() => {
    const filters = presets[preset];
    setContentModules(
      filters
        ? CONTENT_MODULES.filter(
            (module) =>
              (filters.pick && filters.pick.includes(module.type)) ||
              (filters.omit && !filters.omit.includes(module.type)),
          )
        : CONTENT_MODULES,
    );
  }, [preset]);

  return (
    <>
      <Block spaced>
        {label && <Typography.Title level={3}>{label}</Typography.Title>}
        <ContentModulePicker
          contentModules={contentModules}
          eventBlogsList={eventBlogsList}
          onSubmit={handleSubmit}
          allowAdd={allowAdd}
          defaultOpenedType={contentModuleTypeParam}
          buttonLabel="ADD CONTENT MODULE"
          placeholder="- Choose a type of content -"
        />
      </Block>
      <Form.List name={name}>
        {(fields, { move, remove }) => {
          if (fields.length === 0) return null;

          return (
            <Sortable.List
              space="large"
              droppableId="contentModules"
              // TODO: clear content module errors on drag end or do something else to change order in form errors
              onDragEnd={({ source, destination }) => move(source.index, destination.index)}
            >
              {fields.map((field, index) => {
                const key = `${name}-${field.key}`;

                const modules = form.getFieldValue(name);
                const modulesErrors = form.getFieldError(name);

                const module = modules[index];
                const moduleErrors = modulesErrors[index];

                const MODULE = CONTENT_MODULES.find((moduleInList) =>
                  Array.isArray(moduleInList.type)
                    ? moduleInList.type.includes(module.type)
                    : moduleInList.type === module.type,
                );
                // TODO: add a placeholder for missing module
                if (!MODULE) return null;

                const { listItem: ListItem, modal, ...moduleConfig } = MODULE;

                const onSubmit = (newModule, type = moduleConfig.type) => {
                  form.setFields([
                    {
                      name,
                      value: [...modules.slice(0, index), { type, content: newModule }, ...modules.slice(index + 1)],
                    },
                  ]);
                };

                const onDelete = () => showDeleteConfirm({ onOk: () => remove(index) });

                return (
                  <ControlledContentModuleModal
                    eventBlogsList={eventBlogsList}
                    initialValues={module.content}
                    errors={moduleErrors}
                    onSubmit={onSubmit}
                    onDelete={onDelete}
                    type={module.type}
                    component={modal}
                    {...moduleConfig}
                    key={key}
                  >
                    {({ show }) => (
                      <Sortable.ItemWithButton
                        componentBackground
                        onButtonClick={show}
                        buttonTitle="EDIT"
                        draggableId={key}
                        index={index}
                        footer={
                          ListItem.Footer && <ListItem.Footer {...{ parentId, parentType, content: module.content }} />
                        }
                      >
                        <ListItem module={module} errors={moduleErrors} previewImg={moduleConfig.previewImg} />
                      </Sortable.ItemWithButton>
                    )}
                  </ControlledContentModuleModal>
                );
              })}
            </Sortable.List>
          );
        }}
      </Form.List>
    </>
  );
};

export default ContentModulesField;
